/**
 * Utility functions for error handling
 * Provides user-friendly error messages without exposing internal database details
 */

/**
 * Check if the error is a file size error
 */
export const isFileSizeError = (errorMessage: string): boolean => {
  return errorMessage.includes('File size too large') || 
         errorMessage.includes('Maximum file size is 50MB') ||
         errorMessage.includes('MulterError: File too large') ||
         errorMessage.includes('Field value too long') ||
         errorMessage.includes('Field size too large') ||
         errorMessage.includes('annotation images are too large');
};

/**
 * Check if the error is a server error (500)
 */
export const isServerError = (status?: number): boolean => {
  return status === 500;
};

/**
 * Check if the error is a validation error
 */
export const isValidationError = (error: any): boolean => {
  const message = getErrorMessage(error, '');
  return message.includes('Validation failed') ||
         message.includes('invalid') ||
         message.includes('required') ||
         message.includes('format') ||
         (error.response?.status === 400 && message.includes('data'));
};

/**
 * Check if the error is a duplicate entry error
 */
export const isDuplicateError = (error: any): boolean => {
  const message = getErrorMessage(error, '');
  return message.includes('already exists') ||
         message.includes('already taken') ||
         message.includes('Duplicate entry');
};

/**
 * Check if the error is a not found error
 */
export const isNotFoundError = (error: any): boolean => {
  return error.response?.status === 404 ||
         getErrorMessage(error, '').includes('not found');
};

/**
 * Check if the error is a permission/authorization error
 */
export const isPermissionError = (error: any): boolean => {
  return error.response?.status === 403 ||
         getErrorMessage(error, '').includes('permission') ||
         getErrorMessage(error, '').includes('Access denied');
};

/**
 * Owner edit load when the product is soft-deleted (server: 400 + trash message).
 */
export const isProductInTrashError = (error: unknown): boolean => {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status !== 400) return false;
  const message = getErrorMessage(error, '').toLowerCase();
  return message.includes('trash') || message.includes('restore it before editing');
};

/**
 * Get user-friendly error message from API errors (HttpError / legacy axios-shaped { response }).
 * Maps backend error codes to user-friendly messages
 */
export const getErrorMessage = (error: any, fallbackMessage: string): string => {
  // First try to get message from response
  const responseMessage = error.response?.data?.message || error.response?.data?.error;
  
  if (responseMessage) {
    return responseMessage;
  }
  
  // If no response message, check status code and provide generic messages
  const status = error.response?.status;
  
  if (status === 400) {
    return 'The request is invalid. Please check your input and try again.';
  }
  
  if (status === 401) {
    return 'You are not authenticated. Please log in and try again.';
  }
  
  if (status === 403) {
    return 'You do not have permission to perform this action.';
  }
  
  if (status === 404) {
    return 'The requested resource was not found.';
  }
  
  if (status === 409) {
    return 'This resource already exists.';
  }
  
  if (status === 413) {
    return 'The file is too large. Please use a smaller file.';
  }
  
  if (status === 422) {
    return 'The provided data could not be processed.';
  }
  
  if (status === 429) {
    return 'Too many requests. Please wait a moment and try again.';
  }
  
  if (status >= 500) {
    return 'Service temporarily unavailable. Please try again later.';
  }
  
  // Fallback to error message or provided fallback
  return error.message || fallbackMessage || 'An unexpected error occurred. Please try again.';
};

/**
 * Get user-friendly error message with context
 * Provides more specific messages based on error type
 */
export const getUserFriendlyErrorMessage = (error: any, context?: string): string => {
  const baseMessage = getErrorMessage(error, '');
  
  // Add context if provided
  if (context) {
    return `${context}: ${baseMessage}`;
  }
  
  // Check for specific error types and provide more helpful messages
  if (isValidationError(error)) {
    const fieldErrors = error.response?.data?.errors;
    if (fieldErrors && Array.isArray(fieldErrors) && fieldErrors.length > 0) {
      const firstError = fieldErrors[0];
      if (firstError.field && firstError.message) {
        return `${firstError.field}: ${firstError.message}`;
      }
    }
    return 'Please check your input and make sure all required fields are filled correctly.';
  }
  
  if (isDuplicateError(error)) {
    return baseMessage || 'This value already exists. Please use a different one.';
  }
  
  if (isNotFoundError(error)) {
    return 'The requested item was not found. It may have been deleted or moved.';
  }
  
  if (isPermissionError(error)) {
    return 'You do not have permission to perform this action.';
  }
  
  if (isFileSizeError(baseMessage)) {
    return 'The file is too large. Please use a smaller file.';
  }
  
  if (isServerError(error.response?.status)) {
    return 'Service temporarily unavailable. Please try again later.';
  }
  
  return baseMessage;
};
